import {
    Component,
    Host,
    h,
    Element,
    Event,
    Prop,
} from "@stencil/core";
import type { EventEmitter } from "@stencil/core"
import {
    CollapsibleList,
    ConversationCookieLink,
    ConversationDropdown,
    ConversationLink,
    ConversationMessage,
    CookieLink,
    DefaultText,
    TimeoutText,
    InfoText,
    FormFieldType,
    StampText,
    StartThemeStampLabels,
    UserMessage,
} from "../../../../utils/backend/reply-parsed";
import type {
    AnswerBy,
    Conversation
} from "../../../../utils/backend/reply-parsed";
import {
    getCookie,
    setExpiringCookies,
} from "../../../../utils/cookies/cookies";
import type { BotId } from "../../../../global/models/models";
import {
    BOT_IS_LOADING,
    BOT_HAS_TIMEOUT,
    START_THEME_STAMP_PART_1,
    FORM_THEME_STAMP,
    FEEDBACK_GIVEN_STAMP,
    FEEDBACK_WITHDRAWN_STAMP,
} from "../../../../global/variables";

@Component({
    tag: "c-conversation",
    styleUrl: "c-conversation.scss",
})
export class CConversation {
    multiChoiceButtonElements: HTMLElement[] = [];
    latestUserMessage: HTMLElement;
    @Element() hostElement: HTMLElement;
    @Event() cookieButtonSelected: EventEmitter;
    @Event() cookieLinkSelected: EventEmitter;
    @Event() themeChanged: EventEmitter<void>;
    @Prop() loading: boolean;
    @Prop() timeout: boolean;
    @Prop() answerBy: AnswerBy | undefined;
    @Prop() conversation: Conversation = [];
    @Prop() currentBotId: BotId;
    @Prop() formLang?: string;
    @Prop() formType?: string;
    @Prop() formState?: string;

    componentDidRender() {
        if (this.latestUserMessage?.scrollIntoView) {
            this.latestUserMessage.scrollIntoView({
                block: "start",
                behavior: "smooth",
            });
        }
        if (!this.latestUserMessage) {
            // On the first render
            this.hostElement.focus();
        }
        this.restoreFirefoxFocus();
    }

    restoreFirefoxFocus() {
        if (!this.isUsingFirefox() || !this.latestUserMessage) {
            return;
        }
        // Without this solution, Firefox does not remember the last focused element after clicking a multiple choice button.
        // The next time the user hits tab, the first element of the page will be focused instead of the next element inside the bot.
        // To fix this, we temporarily make the default message bubble focusable and secretly focus it without showing a focus highlight.
        // Additionally we need to set :focus {outline: none;}
        this.latestUserMessage.setAttribute("tabindex", "-1");
        this.latestUserMessage.focus();
        this.latestUserMessage.removeAttribute("tabindex");
        // However the solution above makes VoiceOver on Mac loose focus when removing tabindex and starts from the top of conversation.
        // Maybe  screen reader users could just use tab instead, but its not optimal
    }

    isUsingFirefox() {
        return navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    }

    handleCookieLink(cookieLink: CookieLink) {
        // This function is called before <a> opens the link
        if (
            cookieLink.cookies &&
            getCookie("personalization_consent") === "accepted"
        ) {
            setExpiringCookies(cookieLink.cookies);
        }
        this.cookieLinkSelected.emit(cookieLink);
    }

    handleConversationCookieLink(
        conversationCookieLink: ConversationCookieLink
    ) {
        if (
            conversationCookieLink.cookies &&
            getCookie("personalization_consent") === "accepted"
        ) {
            setExpiringCookies(conversationCookieLink.cookies);
        }
    }

    defaultText = (defaultText: DefaultText) => {
        return (
            <div
                class={`message-row ${defaultText.feedback ? "message-row-with-feedback" : ""
                }`}
            >
                <span class="sr-only">Chatbot sagt:</span>
                <c-bubble
                    text={defaultText.text}
                    bubbleId={defaultText.bubbleId}
                    variant="bot"
                    withFeedback={defaultText.feedback}
                ></c-bubble>
            </div>
        );
    };

    timeoutText = (timeoutText: TimeoutText) => {
        return (
            <div
                class={`message-full-row`}
            >
                <span class="sr-only">Leider ist ein Fehler aufgetreten, daher steht der Chatbot derzeit nicht vollständig zur Verfügung. Bitte versuchen Sie es zu einem späteren Zeitpunkt noch einmal.</span>
                <c-bubble
                    text={timeoutText.text}
                    bubbleId={"DIGITAL-ASSISTANT-TIOMEOUTMESSAGE"}
                    variant="timeout"
                ></c-bubble>
            </div>
        );
    };

    infoText = (infoText: InfoText) => {
        return (
            <div
                class={`message-row ${infoText.feedback ? "message-row-with-feedback" : ""
                }`}
            >
                <span class="sr-only">Chatbot sagt:</span>
                <c-bubble-info
                    text={infoText.text}
                    info={infoText.info}
                    bubbleId={infoText.bubbleId}
                    withFeedback={infoText.feedback}
                ></c-bubble-info>
            </div>
        );
    };

    userMessage = (userMessage: UserMessage) => {
        return (
            <div
                class="message-row message-row--user"
                ref={(el) => {
                    if (this.getLatestUserMessage() === userMessage) {
                        this.latestUserMessage = el!;
                    }
                }}
            >
                {/* For users we need an extra empty element so the screen readers reads "Nutzer sagt" before it reads the message.
        We cannot put the aria-label on the avatar, because the avatar comes after the message in the document flow */}
                <span class="sr-only">Nutzer sagt:</span>
                <c-bubble variant="user" text={userMessage.text}></c-bubble>
            </div>
        );
    };

    // Each collapsible has aria label 'Hier klicken/Öffnen um mehr über XZ zu erfahren. Inhalt dann ohne aria live.'
    collapsibleList = (collapsible: CollapsibleList) => {
        return (
            <div
                class={`message-row ${collapsible.feedback ? "message-row-with-feedback" : ""
                }`}
            >
                <c-bubble-collapsible
                    elements={collapsible.elements}
                    bubbleId={collapsible.bubbleId}
                    withFeedback={collapsible.feedback}
                ></c-bubble-collapsible>
            </div>
        );
    };

    conversationCookieLink(conversationCookieLink: ConversationCookieLink) {
        return (
            <a
                class="cookie-link"
                href={conversationCookieLink.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                    this.handleConversationCookieLink(conversationCookieLink)
                }
            >
                <span class="sr-only">
                    Klicken sie für: {conversationCookieLink.screenReader ?? conversationCookieLink.text}
                </span>
            </a>
        );
    }

    getLatestUserMessage() {
        return [...this.conversation]
            .reverse()
            .find(
                (m: ConversationMessage | StampText) =>
                    m.type === "userMessage" ||
                    m.type === "feedbackGivenStamp" ||
                    m.type === "feedbackWithdrawnStamp"
            );
    }

    private getMessageStamp(message: StampText) {
        let stamp = "";
        switch (message.type) {
            case "startThemeStamp":
                stamp = START_THEME_STAMP_PART_1 + " " + StartThemeStampLabels[message.botId];
                break;
            case "formThemeStamp":
                stamp = FORM_THEME_STAMP
                break;
            case "feedbackGivenStamp":
                stamp = FEEDBACK_GIVEN_STAMP;
                break;
            case "feedbackWithdrawnStamp":
                stamp = FEEDBACK_WITHDRAWN_STAMP;
                break;
        }
        return { variant: "start-theme", stamp };
    }

    // Hilfsfunktion für Change-Theme-Stamps
    private getChangeThemeVariant(formLang?: string, formType?: string, formState?: string, currentBotId?: string) {
        const notRelevant = currentBotId === "STARTSEITE" || currentBotId === "PINK";
        if (notRelevant) return null;

        const isFormFlow = currentBotId === "FORM" || currentBotId === "INKASSO";
        if (isFormFlow) {
            const isUkrainian = formLang === "ua";
            const hasFormType = Boolean(formType);

            if (isUkrainian) {
                return hasFormType
                    ? { variant: "form-ua", state: formState }
                    : { variant: "job-form-ua" };
            } else {
                return hasFormType
                    ? { variant: "form", state: formState }
                    : { variant: "job-form" };
            }
        }
        return { variant: "change-theme" };
    }

    // Hauptfunktion für beide Stamp-Fälle
    renderStamp({ message }: { message?: StampText } = {}) {
        if (message) {
            const { variant, stamp } = this.getMessageStamp(message);
            // @ts-ignore
            return (
                <div
                    class="stamp-container"
                    ref={el => {
                        if (this.getLatestUserMessage() === message) {
                            this.latestUserMessage = el!;
                        }
                    }}
                >
                    <c-stamp-text variant={variant as any} stamp={stamp}></c-stamp-text>
                </div>
            );
        }

        const variantObj = this.getChangeThemeVariant(this.formLang, this.formType, this.formState, this.currentBotId);
        if (!variantObj) return null;

        // @ts-ignore
        return (
            <div class="stamp-container">
                <c-stamp-text variant={variantObj.variant as any} state={variantObj.state}></c-stamp-text>
            </div>
        );
    }

    conversationDropdown(message: ConversationDropdown) {
        return (
            <div class={message.feedback ? "message-row-with-feedback" : ""}>
                <div class="buttons-container">
                    <c-dropdown
                        selectDisabled={true}
                        text={message.text}
                        selectPlaceholder={message.selectedOptionLabel}
                        elements={[]}
                        bubbleId={message.bubbleId}
                        withFeedback={message.feedback}
                    ></c-dropdown>
                </div>
            </div>
        );
    }

    getConversation() {
        return this.conversation.map((message) => {
            switch (message.type) {
                case "conversationLink":
                    return this.conversationLink(message);
                case "conversationCookieLink":
                    return this.conversationCookieLink(message);
                case "defaultText":
                    return this.defaultText(message);
                case "timeoutText":
                    return this.timeoutText(message);
                case "infoText":
                    return this.infoText(message);
                case "collapsible":
                    return this.collapsibleList(message);
                case "userMessage":
                    return this.userMessage(message);
                case "startThemeStamp":
                case "formThemeStamp":
                case "feedbackGivenStamp":
                case "feedbackWithdrawnStamp":
                    return this.renderStamp({message});
                case "conversationDropdown":
                    return this.conversationDropdown(message);
            }
        });
    };

    conversationLink = (link: ConversationLink) => {
        return (
            <div class="buttons-container">
                <c-suggestions
                    elements={([] as ConversationLink[]).concat(link)}
                ></c-suggestions>
            </div>
        );
    };

    handleSubmit(e: Event) {
        // Prevent page from reloading when user submits the form
        e.preventDefault();
    }

    getMultipleChoiceButtons = () => {
        if (!this.answerBy) {
            return null;
        }
        switch (this.answerBy.type) {
            case "formInput":
                return (
                    <div class="form-container">
                        <form onSubmit={(e: Event) => this.handleSubmit(e)}>
                            {this.answerBy.elements.map((element) => {
                                let el;
                                switch (element.type) {
                                    case FormFieldType.DATEPICKER:
                                        el = (
                                            <c-form-datepicker
                                                formTitle={element.name}
                                                germanTitle={element.german}
                                            />
                                        );
                                        break;
                                    case FormFieldType.FORM_INPUT:
                                        el = (
                                            <c-form-input
                                                formName={element.name}
                                                germanTitle={element.german}
                                                infoString={element.infoString}
                                            />
                                        );
                                        break;
                                    case FormFieldType.FORM_TEXT:
                                        el = (
                                            <c-form-text
                                                formName={element.name}
                                                germanTitle={element.german}
                                                infoString={element.infoString}
                                            />
                                        );
                                        break;
                                    case FormFieldType.DROPDOWN:
                                        el = (
                                            <c-form-dropdown
                                                elements={element.choice}
                                                formName={element.name}
                                                germanTitle={element.german}
                                            />
                                        );
                                        break;
                                    case FormFieldType.TABLE:
                                        el = (
                                            <c-form-table
                                                tableType={element.keyValue}
                                            />
                                        );
                                        break;
                                    case FormFieldType.RADIO:
                                        el = (
                                            <c-form-radio
                                                elements={element.choice}
                                                formName={element.name}
                                                theme={element.theme}
                                                germanTitle={element.german}
                                            />
                                        );
                                        break;
                                    case FormFieldType.BUTTON:
                                        el = (
                                            <c-suggestions
                                                elements={element.suggestion}
                                                btnContext={element.name}
                                                germanTitle={element.german}
                                                theme={element.theme}
                                                icon={element.icon}
                                            />
                                        );
                                        break;
                                    case FormFieldType.SUBMIT:
                                        el = (
                                            <c-form-button
                                                btnName={element.title}
                                                pageNum={element.pageName}
                                                targetRef={element.payload}
                                                germanTitle={element.german}
                                                theme={element.theme}
                                                icon={element.icon}
                                                stage={element.stage}
                                            />
                                        );
                                        break;
                                    default:
                                        el = (
                                            <p>Unknown Field: {element.type}</p>
                                        );
                                }
                                return el;
                            })}
                        </form>
                    </div>
                );
            case "textInput":
            case "salaryInput":
                return null;
            case "multipleChoice":
                return (
                    <div class="buttons-container">
                        <ul class="no-bullets">
                            <c-multiple-choice-buttons
                                choices={this.answerBy.choices}
                                shortLabels={this.answerBy.shortLabels}
                            ></c-multiple-choice-buttons>
                        </ul>
                    </div>
                );
            case "suggestions":
                return (
                    <div class="buttons-container">
                        <ul class="no-bullets">
                            <c-suggestions
                                elements={this.answerBy.elements}
                                introText={this.answerBy.introText}
                                botId={this.currentBotId}
                            ></c-suggestions>
                        </ul>
                    </div>
                );
            case "cookies": {
                const cookies = this.answerBy.elements.map((cookieElement) => {
                    switch (cookieElement.type) {
                        case "link":
                            return (
                                <a
                                    class="cookie-link"
                                    href={cookieElement.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() =>
                                        this.handleCookieLink(cookieElement)
                                    }
                                >
                                    <span class="sr-only">
                                        Klicken sie für: {cookieElement.screenReader ?? cookieElement.text}
                                    </span>
                                </a>
                            );
                        case "button":
                            return (
                                <button
                                    class="cookie-button"
                                    innerHTML={`<span class="sr-only">Klicken sie für: ${cookieElement.screenReader ??
                                    cookieElement.text
                                    }</span>`}
                                    onClick={() =>
                                        this.cookieButtonSelected.emit(
                                            cookieElement
                                        )
                                    }
                                ></button>
                            );
                    }
                });
                return <div class="buttons-container">{cookies}</div>;
            }
            case "dropdown":
                return (
                    <div class="buttons-container">
                        <c-dropdown
                            text={this.answerBy.text}
                            elements={this.answerBy.choices}
                            bubbleId={this.answerBy.bubbleId}
                            withFeedback={this.answerBy.feedback}
                        ></c-dropdown>
                    </div>
                );
        }
    };

    render() {
        return (
            <Host
                id="cb-conversation"
                role="log"
                tabindex="0"
                aria-label="Nachrichten Verlauf"
                aria-live="assertive"
            >
                {/* Messages */}
                {this.getConversation()}
                {this.getMultipleChoiceButtons()}
                {this.timeout ? (
                    <div
                        id="cb-bot-timeout"
                        class="message-row"
                        aria-label={BOT_HAS_TIMEOUT}
                    >
                        <c-bubble variant="timeout" text={BOT_HAS_TIMEOUT}></c-bubble>
                    </div>
                ) : ""}
                {this.loading ? (
                    <div
                        id="cb-bot-loading"
                        class="message-row"
                        aria-label={BOT_IS_LOADING}
                    >
                        <c-bubble variant="loading"></c-bubble>
                    </div>
                ) : (
                    this.renderStamp()
                )}
            </Host>
        );
    }
}
